import { useEffect, useContext, Fragment, lazy, memo, useCallback } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { AuthLayout, Layout, SuspenseLayout } from './AppRoute';
import { AuthContext } from '../contexts/auth';
import routes from '../constants/routes';
import { useSocket } from '../utils/hooks';
import { isCompanyInit, viewCompany } from '../utils/api/companies';
import { useDispatch } from 'react-redux';
import { setCompanyInit } from '../store/app';
import { useUser } from '../utils/hooks/user';
import { updateAgent } from '../utils/api/user';
import { useEventListener } from 'use-event-listeners';
import { logoutUser } from '../store/auth';
import { toast } from 'react-toastify';
import Forbidden from './Error/Forbidden';
import { withProfiler } from '@sentry/react';

const Overview = lazy(() => import('./Overview'));
const Conversations = lazy(() => import('./Conversations'));
const Reports = lazy(() => import('./Reports/Analytics'));
const Agents = lazy(() => import('./Agents'));
const Settings = lazy(() => import('./Settings'));
const Team = lazy(() => import('./Team'));
const Login = lazy(() => import('./Login'));
const Records = lazy(() => import('./Records'));
const Record = lazy(() => import('./Records/Record'));
const Logs = lazy(() => import('./Reports/Logs'));
const Profile = lazy(() => import('./Profile'));
const ForgotPassword = lazy(() => import('../ForgotPassword/ForgotPassword'));
const ResetPassword = lazy(() => import('../ResetPassword/ResetPassword'));
const Calendar = lazy(() => import('./Calendar'));
const Setup = lazy(() => import('./Setup'));
const TwoFa = lazy(() => import('./TwoFa'));
const Scheduler = lazy(() => import('./Scheduler'));
const DocumentVerification = lazy(() => import('./DocumentVerification'));
const Esignature = lazy(() => import('./Esignature'));
const DocumentUploadPage = lazy(() => import('./Esignature/DocumentUploadPage'));
const DocumentBuild = lazy(() => import('./Esignature/DocumentBuild'));
const EsignatureTemplates = lazy(() => import('./Esignature/Templates'));
const TemplateUploadPage = lazy(() => import('./Esignature/Templates/TemplateUploadPage'));

// Component to handle all the app initialization logic
function AppInitializer() {
  const { authDispatch } = useContext(AuthContext);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const user = useUser();

  const getCompanyDetails = async () => {
    try {
      const response = await viewCompany(user?.company?.id);

      if (response.status === 200) {
        let sessionTimeoutValue = !response?.data?.company?.options?.sessionTimeout
          ? null
          : Number(response?.data?.company?.options?.sessionTimeout) * 1000;

        authDispatch({
          type: 'SESSION_TIMEOUT',
          payload: sessionTimeoutValue
        });
      }
    } catch (error) {
      //
    }
  };

  const checkIsInit = async () => {
    try {
      const {
        data: { status }
      } = await isCompanyInit();
      dispatch(setCompanyInit(status));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (user) {
      getCompanyDetails();
    }

    checkIsInit();
  }, []);

  const updateAgentOnSockketConnect = useCallback(async () => {
    try {
      await updateAgent(user?.id, { is_online: true });
    } catch (error) {
      //
    }
  }, [user, socket.connected]);

  useEffect(() => {
    if (socket.connected && user) {
      updateAgentOnSockketConnect();
    }
  }, [user, socket.connected]);

  const handleNavigation = () => {
    navigate('/auth/login');
    dispatch(logoutUser());
    toast.info('session expired', { autoClose: 2000 });
  };

  useEventListener({
    listeners: {
      'log-out-navigation': () => handleNavigation()
    }
  });

  return <SuspenseLayout />;
}

// Define the router configuration outside of the App component
const router = createBrowserRouter([
  {
    element: <AppInitializer />,
    children: [
      {
        path: '/',
        element: <Layout />,
        children: [
          { path: routes.CONVERSATIONS.pathname, element: <Conversations /> },
          { path: routes.OVERVIEW.pathname, element: <Overview /> },
          { path: routes.RECORDS.pathname, element: <Records /> },
          { path: routes.CALL_REQUESTS.pathname, element: <Calendar /> },
          { path: routes.REPORTS.pathname, element: <Reports /> },
          { path: routes.AGENTS.pathname, element: <Agents /> },
          { path: routes.SETTINGS.pathname, element: <Settings /> },
          { path: routes.LOGS.pathname, element: <Logs /> },
          { path: routes.TEAM.pathname, element: <Team /> },
          { path: routes.RECORD.pathname, element: <Record /> },
          {
            path: routes.PROFILE.pathname,
            element: <Profile />,
            handle: { noHeader: true }
          },
          { path: routes.SCHEDULER.pathname, element: <Scheduler /> },
          { path: routes.TWOFA.pathname, element: <TwoFa /> },
          { path: routes.DOCUMENTS.pathname, element: <DocumentVerification /> },
          { path: routes.ESIGNATURE.pathname, element: <Esignature /> },
          { path: routes.ESIGNATURE_UPLOAD.pathname, element: <DocumentUploadPage /> },
          { path: routes.ESIGNATURE_BUILD.pathname, element: <DocumentBuild /> },
          { path: routes.ESIGNATURE_TEMPLATES.pathname, element: <EsignatureTemplates /> },
          { path: routes.ESIGNATURE_TEMPLATE_UPLOAD.pathname, element: <TemplateUploadPage /> },
          { path: routes.ESIGNATURE_TEMPLATE_BUILD.pathname, element: <DocumentBuild isTemplate /> }
        ]
      },
      {
        element: <AuthLayout />,
        children: [
          { path: routes.SETUP.pathname, element: <Setup /> },
          { path: routes.LOGIN.pathname, element: <Login /> },
          { path: routes.FORGOT_PASSWORD.pathname, element: <ForgotPassword /> },
          { path: routes.RESET_PASSWORD.pathname, element: <ResetPassword /> },
          { path: '/blank', element: <Fragment /> },
          { path: routes.FORBIDDEN.pathname, element: <Forbidden /> }
        ]
      }
    ]
  },
  {
    path: '*',
    element: <Navigate to={routes.OVERVIEW.pathname} replace />
  }
]);

function App() {
  return <RouterProvider router={router} />;
}

export default memo(withProfiler(App));
